import React, {useState} from 'react';
import {useNavigate, generatePath} from "react-router-dom";
import {toast} from "react-toastify";
import ROUTES from "../../../../routes";
import {EnumChecklistState, IChecklistManagementResource} from "../../../../resources/IChecklistManagement.resource";
import ChecklistStatusFactory from "../../../../constants/ChecklistStatusFactory";
import ClickCopyBtn from "../../../AutomaticAssessmentPage/components/ClickCopyBtn";
import ConfirmDialog from "../../../../components/ConfirmDialog";
import DeleteIconButton from "../../../../components/DeleteIconButton";
import FetchService from "../../../../services/api/FetchService";
import CompanyObserver from "../../../../mobx/company/CompanyObserver";
import {ErrorToastSettings} from "../../../../services/toast/toastSettings";
import styles from './styles.module.scss';

interface IProps {
    data: IChecklistManagementResource[];
    onDeleted: (id: number) => void;
}

const ChecklistManagementTable: React.FC<IProps> = ({data, onDeleted}) => {
    const navigate = useNavigate();
    const [deleteTarget, setDeleteTarget] = useState<IChecklistManagementResource | null>(null);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);

    const handleRowClick = (id: number) => {
        navigate(generatePath(ROUTES.CHECKLIST_MANAGEMENT_DETAILS, {id: String(id)}));
    };

    const handleConfirmDelete = async () => {
        const companyId = CompanyObserver.companyId ?? CompanyObserver.getCompanyId();
        if (!deleteTarget || !companyId) return;

        try {
            setIsDeleting(true);
            await FetchService.deleteChecklistManagement(companyId, deleteTarget.id);
            onDeleted(deleteTarget.id);
            setDeleteTarget(null);
        } catch (error) {
            toast.error('Не вдалося видалити чеклист', ErrorToastSettings);
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <div className={styles.table__container}>
            <table
                className={styles.table}
                cellSpacing={0}
                cellPadding={0}
            >
                <thead className={styles.table__thead}>
                <tr>
                    <td className={styles.table__cell_first}>Назва</td>
                    <td className={styles.table__cell_head}>UUID</td>
                    <td className={styles.table__cell_head}>Опис</td>
                    <td className={styles.table__cell_head}>Статус</td>
                    <td className={styles.table__cell_last}/>
                </tr>
                </thead>
                <tbody className={styles.table__tbody}>
                {data.map((item) => {
                    const status = ChecklistStatusFactory(item.state);
                    const isApproved = item.state === EnumChecklistState.APPROVED;
                    return (
                        <tr
                            key={item.id}
                            className={styles.table__row}
                            onClick={() => handleRowClick(item.id)}
                        >
                            <td>{item.name}</td>
                            <td onClick={(e) => e.stopPropagation()}>
                                <ClickCopyBtn text={item.checklistUuid}/>
                            </td>
                            <td>{item.description || '-'}</td>
                            <td>
                                <span
                                    className={styles.table__status}
                                    style={{backgroundColor: status.color}}
                                >
                                    {status.name}
                                </span>
                            </td>
                            <td onClick={(e) => e.stopPropagation()}>
                                {!isApproved ? (
                                    <DeleteIconButton
                                        title={'Видалити чеклист'}
                                        onClick={() => setDeleteTarget(item)}
                                    />
                                ) : null}
                            </td>
                        </tr>
                    );
                })}
                </tbody>
            </table>
            <ConfirmDialog
                open={deleteTarget !== null}
                title={'Видалити чеклист?'}
                text={`Цю дію неможливо скасувати. Чеклист "${deleteTarget?.name ?? ''}" буде видалено.`}
                confirmText={'Видалити'}
                isLoading={isDeleting}
                onConfirm={handleConfirmDelete}
                onCancel={() => setDeleteTarget(null)}
            />
        </div>
    );
};

export default ChecklistManagementTable;
